import { useCallback } from 'react';
import { message } from 'antd';

export type NotificationType = 'success' | 'error' | 'info' | 'warning';

interface NotificationOptions {
  duration?: number; // Duration in seconds
  key?: string; // Unique key to update existing notification
}

/**
 * Hook for showing floating notifications that don't cause layout shifts
 *
 * Uses Ant Design's message API for a better UX:
 * - Fixed position at top of screen
 * - No layout shifts
 * - Appropriate durations based on message type
 * - Can be dismissed by user
 *
 * @example
 * ```tsx
 * const notify = useNotification();
 *
 * notify.success('Item scanned successfully!');
 * notify.error('Insufficient quantity!', { duration: 5 }); // Stays 5 seconds
 * notify.info('Scan destination position');
 * ```
 */
export const useNotification = () => {
  // Configure message globally
  message.config({
    top: 16,
    maxCount: 3, // Show max 3 messages at once
  });

  /**
   * Show a notification
   *
   * Default durations:
   * - success: 2 seconds (quick acknowledgment)
   * - error: 4 seconds (user needs time to read)
   * - info: 3 seconds
   * - warning: 3.5 seconds
   */
  const notify = useCallback((
    type: NotificationType,
    content: string,
    options?: NotificationOptions
  ) => {
    // Default durations based on message type
    const defaultDurations: Record<NotificationType, number> = {
      success: 2,
      error: 6,
      info: 3,
      warning: 3.5,
    };

    const duration = options?.duration ?? defaultDurations[type];
    const key = options?.key;

    switch (type) {
      case 'success':
        message.success({ content, duration, key });
        break;
      case 'error':
        message.error({ content, duration, key });
        break;
      case 'warning':
        message.warning({ content, duration, key });
        break;
      case 'info':
      default:
        message.info({ content, duration, key });
        break;
    }
  }, []);

  // Convenience methods
  const success = useCallback((content: string, options?: NotificationOptions) => {
    notify('success', content, options);
  }, [notify]);

  const error = useCallback((content: string, options?: NotificationOptions) => {
    notify('error', content, options);
  }, [notify]);

  const info = useCallback((content: string, options?: NotificationOptions) => {
    notify('info', content, options);
  }, [notify]);

  const warning = useCallback((content: string, options?: NotificationOptions) => {
    notify('warning', content, options);
  }, [notify]);

  /**
   * Show a loading message that stays until dismissed
   * Returns a function to dismiss the loading message
   */
  const loading = useCallback((content: string = 'Loading...') => {
    const hide = message.loading(content, 0); // 0 = don't auto-dismiss
    return hide;
  }, []);

  /**
   * Dismiss all messages
   */
  const destroy = useCallback(() => {
    message.destroy();
  }, []);

  return {
    notify,
    success,
    error,
    info,
    warning,
    loading,
    destroy,
  };
};
